import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { FaSpinner, FaMapMarkerAlt, FaClock, FaCalendarAlt, FaShieldAlt } from "react-icons/fa";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import useStore from "../../../../store/useStore";
import { Field, SelectField, inputClass } from "./FormFields";
import {
  DEFAULT_PLANS,
  todayIso,
  price,
  getAddressId,
} from "./subscriptionUtils";

/* ─────────────────────────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────────────────────────── */

/** Estimate the number of deliveries in a plan period based on frequency. */
const estimateDeliveries = (durationDays, freqKey) => {
  const days = Number(durationDays) || 30;
  switch (freqKey) {
    case "daily":
      return days;
    case "alternate_day":
      return Math.round(days / 2);
    case "twice_a_day":
      return days * 2;
    case "weekly":
      return Math.max(1, Math.floor(days / 7));
    default:
      return days;
  }
};

const getPlainText = (value = "") =>
  String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const unitToBase = (unit) => {
  const normalized = String(unit || "").trim().toLowerCase();
  if (["ml", "milliliter", "milliliters", "millilitre", "millilitres"].includes(normalized)) {
    return { type: "volume", scale: 0.001 };
  }
  if (["l", "ltr", "litre", "litres", "liter", "liters"].includes(normalized)) {
    return { type: "volume", scale: 1 };
  }
  if (["g", "gram", "grams"].includes(normalized)) return { type: "weight", scale: 0.001 };
  if (["kg", "kilogram", "kilograms"].includes(normalized)) return { type: "weight", scale: 1 };
  return null;
};

const defaultQuantityUnitForVariant = (unit) => {
  const measurement = unitToBase(unit);
  if (measurement?.type === "volume") return "litre";
  if (measurement?.type === "weight") return "kg";
  return String(unit || "litre").trim().toLowerCase();
};

const getVariantUnitsForQuantity = (quantity, quantityUnit, variant) => {
  const variantSize = Number.parseFloat(variant?.variant_name);
  const quantityMeasure = unitToBase(quantityUnit);
  const variantMeasure = unitToBase(variant?.unit_name);
  if (
    !Number.isFinite(variantSize) ||
    variantSize <= 0 ||
    !quantityMeasure ||
    !variantMeasure ||
    quantityMeasure.type !== variantMeasure.type
  ) {
    return Number(quantity) || 0;
  }

  const requestedAmount = (Number(quantity) || 0) * quantityMeasure.scale;
  const variantAmount = variantSize * variantMeasure.scale;
  return variantAmount > 0 ? requestedAmount / variantAmount : Number(quantity) || 0;
};

/* ─────────────────────────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────────────────────────── */

const StartSubscriptionPage = ({
  products,
  addresses,
  plans,
  quantityOptions = [],
  frequencies,
  deliverySlots,
  paymentMethods,
  initialProductId,
  initialVariantId,
  loading,
  productsLoading,
  onSubmit,
}) => {
  const user = useStore((s) => s.user);
  const resolvedPlans = plans?.length ? plans : DEFAULT_PLANS;
  const configuredFrequencies = frequencies?.length
    ? frequencies
    : [{ value: "daily", label: "Daily" }];
  const resolvedFrequencies = configuredFrequencies.some(
    (frequency) => frequency.value === "twice_a_day",
  )
    ? configuredFrequencies
    : [...configuredFrequencies, { value: "twice_a_day", label: "Twice a day" }];
  const configuredSlots = deliverySlots?.length
    ? deliverySlots
    : [{ value: "06:00-08:00", label: "6 AM - 8 AM" }];
  const resolvedSlots = [
    ...configuredSlots,
    ...[
      { value: "06:00-08:00", label: "6 AM - 8 AM" },
      { value: "17:00-19:00", label: "5 PM - 7 PM" },
    ].filter(
      (fallback) =>
        !configuredSlots.some((slot) => slot.value === fallback.value),
    ),
  ];
  const morningSlots = resolvedSlots.filter(
    (slot) => Number.parseInt(slot.value.slice(0, 2), 10) < 12,
  );
  const eveningSlots = resolvedSlots.filter(
    (slot) => Number.parseInt(slot.value.slice(0, 2), 10) >= 12,
  );
  const resolvedMorningSlots = morningSlots.length ? morningSlots : resolvedSlots;
  const resolvedEveningSlots = eveningSlots.length ? eveningSlots : resolvedSlots;
  const resolvedPaymentMethods = paymentMethods?.length
    ? paymentMethods
    : [{ value: "monthly_billing", label: "Monthly Billing" }];

  const [form, setForm] = useState({
    product_id: initialProductId || "",
    product_variant_id: initialVariantId || "",
    address_id: "",
    quantity: 1,
    quantity_unit: "litre",
    morning_slot: "06:00-08:00",
    morning_quantity: 1,
    evening_slot: "17:00-19:00",
    evening_quantity: 1,
    frequency: resolvedFrequencies[0]?.value || "daily",
    plan_type: resolvedPlans[0]?.value || "monthly",
    delivery_slot: resolvedSlots[0]?.value || "06:00-08:00",
    payment_method: "",
    start_date: todayIso(),
  });

  const [changeAddressOpen, setChangeAddressOpen] = useState(false);

  const selectedProduct = useMemo(
    () =>
      products.find((p) => String(p.product_id) === String(form.product_id)),
    [products, form.product_id],
  );

  const selectedAddress = useMemo(
    () => addresses.find((a) => String(getAddressId(a)) === String(form.address_id)),
    [addresses, form.address_id],
  );

  const selectedVariant = useMemo(
    () =>
      selectedProduct?.variants?.find(
        (v) => String(v.product_variant_id) === String(form.product_variant_id),
      ),
    [selectedProduct, form.product_variant_id],
  );

  const selectedPlan = useMemo(
    () => resolvedPlans.find((p) => p.value === form.plan_type),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [resolvedPlans, form.plan_type],
  );

  /* ── Auto-select product ── */
  useEffect(() => {
    const exists = products.some(
      (p) => String(p.product_id) === String(form.product_id),
    );
    if (products.length > 0 && (!form.product_id || !exists)) {
      setForm((cur) => ({ ...cur, product_id: products[0].product_id }));
    }
  }, [products, form.product_id]);

  /* ── Auto-select variant ── */
  useEffect(() => {
    if (!selectedProduct?.variants?.length) return;
    const hasVariant = selectedProduct.variants.some(
      (v) => String(v.product_variant_id) === String(form.product_variant_id),
    );
    if (!hasVariant) {
      setForm((cur) => ({
        ...cur,
        product_variant_id: selectedProduct.variants[0].product_variant_id,
      }));
    }
  }, [selectedProduct, form.product_variant_id]);

  /* ── Auto-select address ── */
  useEffect(() => {
    if (!form.address_id && addresses.length > 0) {
      const def = addresses.find((a) => a.is_default) || addresses[0];
      setForm((cur) => ({ ...cur, address_id: getAddressId(def) }));
    }
  }, [addresses, form.address_id]);

  /* ── Sync unit when variant changes ── */
  useEffect(() => {
    if (!selectedVariant) return;
    setForm((cur) => ({
      ...cur,
      quantity: 1,
      quantity_unit: defaultQuantityUnitForVariant(selectedVariant.unit_name),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant?.product_variant_id]);

  /* ── Re-sync defaults when config lists arrive ── */
  useEffect(() => {
    setForm((cur) => {
      const freqValid = resolvedFrequencies.some(
        (f) => f.value === cur.frequency,
      );
      const planValid = resolvedPlans.some((p) => p.value === cur.plan_type);
      const slotValid = resolvedSlots.some(
        (s) => s.value === cur.delivery_slot,
      );
      const pmValid = resolvedPaymentMethods.some(
        (m) => m.value === cur.payment_method,
      );
      return {
        ...cur,
        frequency: freqValid
          ? cur.frequency
          : resolvedFrequencies[0]?.value || cur.frequency,
        plan_type: planValid
          ? cur.plan_type
          : resolvedPlans[0]?.value || cur.plan_type,
        delivery_slot: slotValid
          ? cur.delivery_slot
          : resolvedSlots[0]?.value || cur.delivery_slot,
        morning_slot: resolvedMorningSlots.some((slot) => slot.value === cur.morning_slot)
          ? cur.morning_slot
          : resolvedMorningSlots[0]?.value || cur.morning_slot,
        evening_slot: resolvedEveningSlots.some((slot) => slot.value === cur.evening_slot)
          ? cur.evening_slot
          : resolvedEveningSlots.find((slot) => slot.value === "17:00-19:00")?.value ||
            resolvedEveningSlots[0]?.value ||
            cur.evening_slot,
        payment_method: pmValid
          ? cur.payment_method
          : "",
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    frequencies?.length,
    plans?.length,
    deliverySlots?.length,
    paymentMethods?.length,
  ]);

  const update = (name, value) => setForm((cur) => ({ ...cur, [name]: value }));

  const updatePaymentMethod = (paymentMethod) =>
    setForm((current) => ({
      ...current,
      payment_method: paymentMethod,
    }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedProduct || !selectedVariant) {
      toast.error("Select a subscription product and size");
      return;
    }
    if (!form.address_id) {
      toast.error("Add a delivery address first");
      return;
    }
    if (!form.payment_method) {
      toast.error("Please select a payment method");
      return;
    }
    if (
      form.frequency === "twice_a_day" &&
      (Number(form.morning_quantity) <= 0 || Number(form.evening_quantity) <= 0)
    ) {
      toast.error("Morning and evening quantities must be greater than zero");
      return;
    }
    if (form.frequency !== "twice_a_day" && Number(form.quantity) <= 0) {
      toast.error("Quantity must be greater than zero");
      return;
    }
    const sanitizedForm = {
      ...form,
      quantity: Math.max(1, Math.round(Number(form.quantity || 1))),
      morning_quantity: Math.max(1, Math.round(Number(form.morning_quantity || 1))),
      evening_quantity: Math.max(1, Math.round(Number(form.evening_quantity || 1))),
    };
    onSubmit(sanitizedForm);
  };

  const renderQuantityOptions = (fieldName) => {
    const validOptions = quantityOptions.filter(
      (option) =>
        option.unit === form.quantity_unit &&
        Number.isInteger(Number(option.value)) &&
        Number(option.value) >= 1
    );
    if (!validOptions.length) return null;
    return (
      <div className="flex flex-wrap gap-2">
        {validOptions.map((option) => {
          const optValue = Math.round(Number(option.value));
          const selected =
            Number(form[fieldName]) === optValue &&
            form.quantity_unit === option.unit;
          return (
            <button
              key={option.quantity_id ?? `${option.value}-${option.unit}`}
              type="button"
              onClick={() =>
                setForm((current) => ({
                  ...current,
                  [fieldName]: optValue,
                  quantity_unit: option.unit,
                }))
              }
              className={`rounded-full border px-3 py-1.5 text-xs font-bold transition ${
                selected
                  ? "border-primary-500 bg-primary-50 text-primary-700"
                  : "border-gray-200 bg-white text-gray-600 hover:border-primary-300"
              }`}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    );
  };



  return (
    <div className="min-h-[calc(100vh-120px)] bg-gradient-to-b from-emerald-50/70 via-white to-white">
      <main className="mx-auto max-w-7xl px-4 py-7 sm:px-6 sm:py-10 lg:px-8">
        <form id="start-subscription-form" onSubmit={handleSubmit}>
          <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1.25fr)_minmax(340px,0.75fr)]">
            {/* ── Left column ── */}
            <div className="space-y-6">
              <section className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
                <div className="mb-4 flex items-end justify-between gap-3">
                  <div>
                    <p className="text-xs font-extrabold uppercase tracking-wider text-primary-700">
                      Explore the range
                    </p>
                    <h2 className="mt-1 text-lg font-extrabold text-gray-900">
                      Subscription products
                    </h2>
                  </div>
                  <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-bold text-gray-500">
                    {products.length} products
                  </span>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {products.length === 0 && (
                    <div className="col-span-full rounded-xl border border-dashed border-gray-200 py-6 text-center text-xs font-semibold text-gray-400">
                      {productsLoading
                        ? "Loading products…"
                        : "No subscription products are available right now."}
                    </div>
                  )}
                  {products.map((product) => {
                    const selected =
                      String(product.product_id) === String(form.product_id);
                    return (
                      <button
                        key={product.product_id}
                        type="button"
                        onClick={() => update("product_id", product.product_id)}
                        className={`group flex min-h-[148px] w-full gap-3 rounded-2xl border p-3 text-left transition ${
                          selected
                            ? "border-primary-500 bg-primary-50 shadow-md shadow-primary-100"
                            : "border-gray-200 bg-white hover:border-primary-300"
                        }`}
                      >
                        <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-emerald-50 to-gray-50 p-2">
                          {product.image ? (
                            <img
                              src={product.image}
                              alt={product.name}
                              className="h-full w-full object-contain"
                            />
                          ) : (
                            <Inventory2OutlinedIcon className="text-gray-300" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1 py-1">
                          <p className="line-clamp-2 text-sm font-extrabold text-gray-900">
                            {product.name}
                          </p>
                          {product.category_name && (
                            <p className="mt-0.5 text-xs font-semibold text-gray-400">
                              {product.category_name}
                            </p>
                          )}
                          {product.description && (
                            <p className="mt-1 line-clamp-2 text-[11px] leading-4 text-gray-500">
                              {getPlainText(product.description)}
                            </p>
                          )}
                          <div className="mt-2 flex flex-wrap gap-1">
                            {(product.variants || []).map((variant) => (
                              <span
                                key={variant.product_variant_id}
                                className="rounded-md bg-gray-100 px-1.5 py-1 text-[10px] font-bold text-gray-600"
                              >
                                {variant.variant_name}
                                {variant.unit_name
                                  ? ` ${variant.unit_name}`
                                  : ""}{" "}
                                · {price(variant.discount_price)}
                              </span>
                            ))}
                          </div>
                        </div>
                        {selected && (
                          <span className="shrink-0 self-center text-lg font-extrabold text-primary-600">
                            ✓
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </section>

              <section className="rounded-3xl border border-gray-100 bg-white p-4 shadow-sm sm:p-6">
                <Field label="Choose size / variant">
                  <div className="flex flex-wrap gap-2">
                    {(selectedProduct?.variants || []).length === 0 && (
                      <p className="text-xs font-semibold text-gray-400">
                        Select a product above
                      </p>
                    )}
                    {(selectedProduct?.variants || []).map((variant) => {
                      const selected =
                        String(variant.product_variant_id) ===
                        String(form.product_variant_id);
                      return (
                        <button
                          key={variant.product_variant_id}
                          type="button"
                          onClick={() => {
                            update("product_variant_id", variant.product_variant_id);
                          }}
                          className={`rounded-xl border px-4 py-3 text-left transition ${
                            selected
                              ? "border-primary-500 bg-primary-50 text-primary-700"
                              : "border-gray-200 bg-white text-gray-700 hover:border-primary-300"
                          }`}
                        >
                          <p className="text-sm font-extrabold">
                            {variant.variant_name}
                            {variant.unit_name ? ` ${variant.unit_name}` : ""}
                          </p>
                          <p className="mt-0.5 text-xs font-bold">
                            {price(variant.discount_price)}
                            {Number(variant.original_price) >
                              Number(variant.discount_price) && (
                              <span className="ml-2 text-gray-400 line-through">
                                {price(variant.original_price)}
                              </span>
                            )}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </Field>

                {selectedProduct && (
                  <div className="mt-4 rounded-2xl bg-gray-50 p-4 sm:p-5">
                    <div className="flex items-start gap-4">
                      <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl bg-white p-2">
                        {selectedProduct.image ? (
                          <img
                            src={selectedProduct.image}
                            alt={selectedProduct.name}
                            className="h-full w-full object-contain"
                          />
                        ) : (
                          <Inventory2OutlinedIcon className="text-gray-300" />
                        )}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-extrabold text-gray-900">
                          {selectedProduct.name}
                        </h3>
                        {selectedProduct.category_name && (
                          <p className="mt-1 text-xs font-semibold text-primary-700">
                            {selectedProduct.category_name}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="mt-5 border-t border-gray-200 pt-4">
                      <h4 className="mb-2 text-xs font-extrabold uppercase tracking-wider text-gray-500">
                        Product details
                      </h4>
                      {selectedProduct.description ? (
                        <div
                          className="prose prose-sm max-w-none text-gray-600 prose-p:my-2 prose-p:leading-6 prose-strong:text-gray-900 prose-ul:my-2 prose-li:my-1 prose-li:marker:text-primary-600"
                          dangerouslySetInnerHTML={{
                            __html: selectedProduct.description,
                          }}
                        />
                      ) : (
                        <p className="text-sm leading-6 text-gray-500">
                          Select your preferred pack size and delivery plan for
                          this product.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </section>
            </div>

            {/* ── Right column ── */}
            <div className="space-y-4 lg:sticky lg:top-6">
              {/* Card 1: Delivery Address */}
              <div className="rounded-3xl border border-gray-200/80 bg-white p-5 shadow-sm transition">
                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="flex h-7 w-7 items-center justify-center rounded-xl bg-emerald-50 text-[#046a38]">
                      <FaMapMarkerAlt className="text-xs" />
                    </span>
                    <h3 className="text-xs font-black uppercase tracking-wider text-gray-800">
                      Delivery Address
                    </h3>
                  </div>
                  {addresses.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setChangeAddressOpen((v) => !v)}
                      className="text-xs font-bold text-[#046a38] hover:underline"
                    >
                      {changeAddressOpen ? "Done" : "Change"}
                    </button>
                  )}
                </div>

                {selectedAddress ? (
                  <div className="rounded-2xl border border-gray-100 bg-gray-50/70 p-3.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <span className="rounded-md bg-white border border-gray-200 px-2 py-0.5 text-[11px] font-extrabold uppercase text-gray-800 shadow-2xs">
                          {selectedAddress.address_type || "Home"}
                        </span>
                        {selectedAddress.is_default && (
                          <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-extrabold text-[#046a38]">
                            Default
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Recipient Name & Mobile */}
                    {Boolean(
                      selectedAddress.name ||
                        user?.name ||
                        selectedAddress.mobile ||
                        selectedAddress.phone ||
                        user?.phone ||
                        user?.mobile
                    ) && (
                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                        {Boolean(selectedAddress.name || user?.name) && (
                          <div className="flex items-center gap-1.5 font-extrabold text-gray-900">
                            <PersonOutlineOutlinedIcon style={{ fontSize: 15 }} className="text-[#046a38] shrink-0" />
                            <span>{selectedAddress.name || user?.name}</span>
                          </div>
                        )}
                        {Boolean(selectedAddress.mobile || selectedAddress.phone || user?.phone || user?.mobile) && (
                          <div className="flex items-center gap-1.5 font-semibold text-gray-700">
                            <PhoneOutlinedIcon style={{ fontSize: 14 }} className="text-[#046a38] shrink-0" />
                            <a
                              href={`tel:${selectedAddress.mobile || selectedAddress.phone || user?.phone || user?.mobile}`}
                              className="hover:text-[#046a38] hover:underline"
                            >
                              {selectedAddress.mobile || selectedAddress.phone || user?.phone || user?.mobile}
                            </a>
                          </div>
                        )}
                      </div>
                    )}

                    <p className="mt-1.5 text-xs leading-relaxed text-gray-600">
                      {[selectedAddress.address, selectedAddress.city, selectedAddress.state, selectedAddress.pincode]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-amber-200 bg-amber-50/50 p-4 text-center">
                    <p className="text-xs font-bold text-amber-800">No saved address found</p>
                    <p className="mt-0.5 text-[11px] text-gray-500">Please add an address in your profile to proceed.</p>
                  </div>
                )}

                {/* Address Switcher Accordion */}
                {changeAddressOpen && addresses.length > 1 && (
                  <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
                    <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">
                      Select delivery location:
                    </p>
                    <div className="max-h-48 space-y-2 overflow-y-auto pr-1">
                      {addresses.map((address) => {
                        const addrId = getAddressId(address);
                        const isSelected = String(form.address_id) === String(addrId);
                        const addrName = address.name || user?.name;
                        const addrPhone = address.mobile || address.phone || user?.phone || user?.mobile;
                        return (
                          <button
                            key={addrId}
                            type="button"
                            onClick={() => {
                              update("address_id", addrId);
                              setChangeAddressOpen(false);
                            }}
                            className={`w-full rounded-xl border p-2.5 text-left transition ${
                              isSelected
                                ? "border-[#046a38] bg-emerald-50/70 font-semibold"
                                : "border-gray-200 bg-white hover:border-gray-300"
                            }`}
                          >
                            <div className="flex items-center justify-between text-xs">
                              <span className="font-extrabold uppercase text-gray-900">
                                {address.address_type || "Address"}
                              </span>
                              {isSelected ? (
                                <span className="font-extrabold text-[#046a38]">✓ Selected</span>
                              ) : address.is_default ? (
                                <span className="text-[10px] font-bold text-gray-400">Default</span>
                              ) : null}
                            </div>
                            {(addrName || addrPhone) && (
                              <div className="mt-1 flex flex-wrap items-center gap-x-2 text-[11px] font-bold text-gray-800">
                                {addrName && <span>{addrName}</span>}
                                {addrPhone && <span className="font-normal text-gray-500">• {addrPhone}</span>}
                              </div>
                            )}
                            <p className="mt-0.5 line-clamp-1 text-[11px] text-gray-500">
                              {[address.address, address.city, address.pincode].filter(Boolean).join(", ")}
                            </p>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* Card 2: Schedule & Quantity */}
              <div className="rounded-3xl border border-gray-200/80 bg-white p-5 shadow-sm space-y-4">
                <div className="flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-xl bg-emerald-50 text-[#046a38]">
                    <FaClock className="text-xs" />
                  </span>
                  <h3 className="text-xs font-black uppercase tracking-wider text-gray-800">
                    Schedule & Quantity
                  </h3>
                </div>

                {/* Delivery Frequency Pills */}
                <div>
                  <label className="mb-2 block text-xs font-bold text-gray-700">
                    Delivery Frequency
                  </label>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    {resolvedFrequencies.map((option) => {
                      const isSelected = form.frequency === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => update("frequency", option.value)}
                          className={`rounded-xl py-2.5 px-2 text-center text-xs transition ${
                            isSelected
                              ? "bg-[#046a38] text-white font-extrabold shadow-sm ring-2 ring-[#046a38]/20"
                              : "border border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700 font-bold"
                          }`}
                        >
                          {option.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* If Twice a day */}
                {form.frequency === "twice_a_day" ? (
                  <div className="space-y-3 pt-1">
                    {/* Morning */}
                    <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-3.5 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-xs font-extrabold text-amber-950">
                          <span>🌅</span> Morning Delivery
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => update("morning_quantity", Math.max(1, Number(form.morning_quantity || 1) - 1))}
                            disabled={Number(form.morning_quantity) <= 1}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-amber-300 bg-white text-base font-bold text-gray-700 hover:bg-amber-50 disabled:opacity-40"
                          >
                            −
                          </button>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={form.morning_quantity}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === "") {
                                update("morning_quantity", "");
                                return;
                              }
                              const parsed = parseInt(val, 10);
                              update("morning_quantity", isNaN(parsed) ? 1 : Math.max(1, parsed));
                            }}
                            onBlur={() => {
                              if (!form.morning_quantity || Number(form.morning_quantity) < 1) update("morning_quantity", 1);
                            }}
                            className="h-8 w-12 rounded-lg border border-amber-300 bg-white text-center text-xs font-black text-gray-900"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => update("morning_quantity", Number(form.morning_quantity || 1) + 1)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-amber-300 bg-white text-base font-bold text-gray-700 hover:bg-amber-50"
                          >
                            +
                          </button>
                          <span className="text-xs font-bold text-amber-950 ml-0.5">{form.quantity_unit}</span>
                        </div>
                      </div>
                      <div>
                        <label className="mb-1 block text-[11px] font-bold text-amber-900/80">Morning Time Slot</label>
                        <SelectField value={form.morning_slot} onChange={(e) => update("morning_slot", e.target.value)}>
                          {resolvedMorningSlots.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </SelectField>
                      </div>
                      {renderQuantityOptions("morning_quantity")}
                    </div>

                    {/* Evening */}
                    <div className="rounded-2xl border border-indigo-200 bg-indigo-50/40 p-3.5 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="flex items-center gap-1.5 text-xs font-extrabold text-indigo-950">
                          <span>🌆</span> Evening Delivery
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => update("evening_quantity", Math.max(1, Number(form.evening_quantity || 1) - 1))}
                            disabled={Number(form.evening_quantity) <= 1}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-indigo-300 bg-white text-base font-bold text-gray-700 hover:bg-indigo-50 disabled:opacity-40"
                          >
                            −
                          </button>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={form.evening_quantity}
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === "") {
                                update("evening_quantity", "");
                                return;
                              }
                              const parsed = parseInt(val, 10);
                              update("evening_quantity", isNaN(parsed) ? 1 : Math.max(1, parsed));
                            }}
                            onBlur={() => {
                              if (!form.evening_quantity || Number(form.evening_quantity) < 1) update("evening_quantity", 1);
                            }}
                            className="h-8 w-12 rounded-lg border border-indigo-300 bg-white text-center text-xs font-black text-gray-900"
                            required
                          />
                          <button
                            type="button"
                            onClick={() => update("evening_quantity", Number(form.evening_quantity || 1) + 1)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-indigo-300 bg-white text-base font-bold text-gray-700 hover:bg-indigo-50"
                          >
                            +
                          </button>
                          <span className="text-xs font-bold text-indigo-950 ml-0.5">{form.quantity_unit}</span>
                        </div>
                      </div>
                      <div>
                        <label className="mb-1 block text-[11px] font-bold text-indigo-900/80">Evening Time Slot</label>
                        <SelectField value={form.evening_slot} onChange={(e) => update("evening_slot", e.target.value)}>
                          {resolvedEveningSlots.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </SelectField>
                      </div>
                      {renderQuantityOptions("evening_quantity")}
                    </div>
                  </div>
                ) : (
                  /* Single Quantity */
                  <div className="rounded-2xl border border-gray-100 bg-gray-50/80 p-3.5 space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="block text-xs font-black text-gray-900">
                          Quantity per Delivery
                        </span>
                        <span className="text-[11px] font-semibold text-gray-500">
                          Unit: {form.quantity_unit || "litre"}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => update("quantity", Math.max(1, Number(form.quantity || 1) - 1))}
                          disabled={Number(form.quantity) <= 1}
                          className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-base font-bold text-gray-700 transition hover:bg-gray-100 disabled:opacity-40"
                        >
                          −
                        </button>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={form.quantity}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === "") {
                              update("quantity", "");
                              return;
                            }
                            const parsed = parseInt(val, 10);
                            update("quantity", isNaN(parsed) ? 1 : Math.max(1, parsed));
                          }}
                          onBlur={() => {
                            if (!form.quantity || Number(form.quantity) < 1) update("quantity", 1);
                          }}
                          className="h-9 w-14 rounded-xl border border-gray-200 bg-white text-center text-sm font-black text-gray-900 outline-none focus:border-[#046a38] focus:ring-1 focus:ring-[#046a38]"
                          required
                        />
                        <button
                          type="button"
                          onClick={() => update("quantity", Number(form.quantity || 1) + 1)}
                          className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-base font-bold text-gray-700 transition hover:bg-gray-100"
                        >
                          +
                        </button>
                      </div>
                    </div>
                    {renderQuantityOptions("quantity")}
                  </div>
                )}

                {/* Slot & Date Grid */}
                <div className="grid gap-3 sm:grid-cols-2 pt-1">
                  {form.frequency !== "twice_a_day" && (
                    <div>
                      <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-gray-700">
                        <FaClock className="text-[#046a38] text-xs" />
                        Delivery Slot
                      </label>
                      <SelectField value={form.delivery_slot} onChange={(e) => update("delivery_slot", e.target.value)}>
                        {resolvedSlots.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </SelectField>
                    </div>
                  )}
                  <div className={form.frequency === "twice_a_day" ? "sm:col-span-2" : ""}>
                    <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-gray-700">
                      <FaCalendarAlt className="text-[#046a38] text-xs" />
                      First Delivery Date
                    </label>
                    <input
                      type="date"
                      min={todayIso()}
                      value={form.start_date}
                      onChange={(e) => update("start_date", e.target.value)}
                      className={inputClass}
                    />
                  </div>
                </div>

                {/* Plan Type */}
                <div>
                  <label className="mb-1.5 flex items-center gap-1.5 text-xs font-bold text-gray-700">
                    <LocalShippingOutlinedIcon style={{ fontSize: 16 }} className="text-[#046a38]" />
                    Subscription Plan / Duration
                  </label>
                  <SelectField value={form.plan_type} onChange={(e) => update("plan_type", e.target.value)}>
                    {resolvedPlans.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                        {option.duration_days ? ` (${option.duration_days} days)` : ""}
                      </option>
                    ))}
                  </SelectField>
                </div>
              </div>

              {/* Card 3: Payment Method & Order Summary */}
              <div className="rounded-3xl border border-gray-200/80 bg-white p-5 shadow-sm space-y-4">
                <div className="space-y-2">
                  <label className="block text-xs font-black uppercase tracking-wider text-gray-800">
                    Payment Method
                  </label>
                  <div className="space-y-2">
                    {resolvedPaymentMethods.map((option) => {
                      const isSelected = form.payment_method === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => updatePaymentMethod(option.value)}
                          className={`w-full flex items-center justify-between rounded-2xl border p-3.5 text-left transition ${
                            isSelected
                              ? "border-[#046a38] bg-emerald-50/70 ring-1 ring-[#046a38]/30 shadow-xs"
                              : "border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <span
                              className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                                isSelected ? "border-[#046a38] bg-[#046a38]" : "border-gray-300 bg-white"
                              }`}
                            >
                              {isSelected && <span className="h-1.5 w-1.5 rounded-full bg-white" />}
                            </span>
                            <div>
                              <p className="text-xs font-extrabold text-gray-900">{option.label}</p>
                              <p className="text-[11px] text-gray-500">
                                {option.value === "upi_autopay"
                                  ? "Auto-debit via UPI (GPay/PhonePe/Paytm)"
                                  : option.value === "monthly_billing"
                                  ? "Consolidated invoice at month end"
                                  : "Pay at delivery"}
                              </p>
                            </div>
                          </div>
                          {option.is_recommended && (
                            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-extrabold text-emerald-800">
                              Recommended
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Cost Breakdown */}
                {selectedVariant && (
                  <div className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/50 via-white to-gray-50/50 p-4 space-y-2.5 text-xs">
                    <div className="flex items-center justify-between text-gray-600">
                      <span>Rate per delivery</span>
                      <span className="font-extrabold text-gray-900">
                        {price(
                          Number(selectedVariant.discount_price || 0) *
                            (form.frequency === "twice_a_day"
                              ? Number(form.morning_quantity || 1) + Number(form.evening_quantity || 1)
                              : Number(form.quantity || 1))
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-gray-600">
                      <span>
                        Estimated deliveries ({selectedPlan?.label || form.plan_type} · {selectedPlan?.duration_days ?? 30}d)
                      </span>
                      <span className="font-extrabold text-gray-900">
                        ~{estimateDeliveries(selectedPlan?.duration_days ?? 30, form.frequency)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between border-t border-emerald-100/80 pt-2.5 text-sm">
                      <span className="font-extrabold text-gray-800">Est. Total for Plan</span>
                      <span className="text-base font-black text-[#046a38]">
                        {price(
                          Number(selectedVariant.discount_price || 0) *
                            (form.frequency === "twice_a_day"
                              ? getVariantUnitsForQuantity(form.morning_quantity, form.quantity_unit, selectedVariant) +
                                getVariantUnitsForQuantity(form.evening_quantity, form.quantity_unit, selectedVariant)
                              : getVariantUnitsForQuantity(form.quantity, form.quantity_unit, selectedVariant)) *
                            estimateDeliveries(selectedPlan?.duration_days ?? 30, form.frequency)
                        )}
                      </span>
                    </div>
                  </div>
                )}

                {/* CTA Button */}
                <button
                  type="submit"
                  disabled={loading || !selectedVariant || !form.address_id}
                  className="w-full flex items-center justify-center gap-2 rounded-2xl bg-[#046a38] hover:bg-[#03522b] px-5 py-3.5 text-sm font-extrabold text-white shadow-lg shadow-emerald-900/15 transition active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? (
                    <FaSpinner className="h-4 w-4 animate-spin" />
                  ) : !form.payment_method ? (
                    "Select Payment Method to Continue"
                  ) : form.payment_method === "upi_autopay" ? (
                    "Set up AutoPay & Subscribe →"
                  ) : (
                    "Activate Subscription →"
                  )}
                </button>

                <div className="flex items-center justify-center gap-2 text-[11px] font-semibold text-gray-500 pt-0.5">
                  <FaShieldAlt className="text-[#046a38]" />
                  <span>Safe & Secure Checkout · Pause or cancel anytime</span>
                </div>
              </div>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
};

export default StartSubscriptionPage;
